import React, { useCallback, useEffect, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { inquiryApi } from '../../api/inquiryApi';
import { AdminLayout } from '../../components/layouts';
import {
  Alert,
  Button,
  EmptyState,
  Input,
  Select,
  TabButton,
  TableShell,
  Tabs,
  tableClassName,
  tdClassName,
  thClassName,
} from '../../components/ui';

const STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'new', label: 'New' },
  { key: 'contacted', label: 'Contacted' },
  { key: 'archived', label: 'Archived' },
];

const STATUS_STYLES = {
  new: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  contacted: 'bg-blue-50 text-blue-700 ring-blue-100',
  archived: 'bg-slate-100 text-slate-600 ring-slate-200',
};

const INTEREST_LABEL = { client: 'Client', provider: 'Gigger', other: '—' };

const fmtDate = (date) =>
  new Date(date).toLocaleDateString([], {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

const AdminInquiries = () => {
  const [inquiries, setInquiries] = useState([]);
  const [counts, setCounts] = useState({ new: 0, contacted: 0, archived: 0 });
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (filter !== 'all') params.status = filter;
      if (search.trim()) params.search = search.trim();
      const response = await inquiryApi.list(params);
      setInquiries(response.data?.data || []);
      setCounts(response.data?.meta?.counts || { new: 0, contacted: 0, archived: 0 });
      setTotal(response.data?.meta?.total || 0);
    } catch (err) {
      setError(err.message || 'Failed to load inquiries.');
    } finally {
      setLoading(false);
    }
  }, [filter, search]);

  useEffect(() => {
    const timer = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [load, search]);

  const changeStatus = async (id, status) => {
    setInquiries((current) => current.map((inquiry) => (inquiry._id === id ? { ...inquiry, status } : inquiry)));
    try {
      await inquiryApi.updateStatus(id, { status });
    } finally {
      load();
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this inquiry? This cannot be undone.')) return;
    setInquiries((current) => current.filter((inquiry) => inquiry._id !== id));
    try {
      await inquiryApi.remove(id);
    } finally {
      load();
    }
  };

  const tabCount = (key) => key === 'all' ? total : counts[key] != null ? counts[key] : 0;

  return (
    <AdminLayout title="Inquiries">
      <div className="mb-6">
        <h2 className="m-0 font-nunito text-[24px] font-black tracking-[-0.025em] text-giggs-ink sm:text-[28px]">Customer inquiries</h2>
        <p className="mb-0 mt-1 text-sm leading-6 text-giggs-muted">Review incoming questions and keep follow-up status organised.</p>
      </div>

      <div className="mb-5 flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <Tabs>
          {STATUS_TABS.map((tab) => (
            <TabButton
              key={tab.key}
              active={filter === tab.key}
              count={tabCount(tab.key)}
              onClick={() => setFilter(tab.key)}
            >
              {tab.label}
            </TabButton>
          ))}
        </Tabs>

        <Input
          className="xl:max-w-[320px]"
          placeholder="Search name, email, service…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      {error && <Alert variant="danger" className="mb-4">{error}</Alert>}

      <TableShell>
        <table className={`${tableClassName} min-w-[920px]`}>
          <thead>
            <tr>
              <th className={thClassName}>Name</th>
              <th className={thClassName}>Contact</th>
              <th className={thClassName}>Interested As</th>
              <th className={thClassName}>Service</th>
              <th className={thClassName}>Location</th>
              <th className={thClassName}>Submitted</th>
              <th className={thClassName}>Status</th>
              <th className={thClassName}>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td className="px-4 py-14" colSpan={8}><EmptyState title="Loading inquiries…" /></td></tr>
            ) : inquiries.length === 0 ? (
              <tr>
                <td className="px-4 py-10" colSpan={8}>
                  <EmptyState title="No inquiries found" description={filter !== 'all' ? `There are no inquiries marked “${filter}”.` : 'New inquiries will appear here.'} />
                </td>
              </tr>
            ) : (
              inquiries.map((inquiry) => (
                <tr key={inquiry._id} className="hover:bg-[#F8FAF9]">
                  <td className={tdClassName}><div className="font-bold text-giggs-ink">{inquiry.name}</div></td>
                  <td className={tdClassName}>
                    <a href={`mailto:${inquiry.email}`} className="break-all font-semibold text-giggs-green no-underline transition-colors hover:text-giggs-green-hover">{inquiry.email}</a>
                    {inquiry.phone && <div className="mt-1 text-xs text-giggs-muted">{inquiry.phone}</div>}
                  </td>
                  <td className={tdClassName}>{INTEREST_LABEL[inquiry.interestedAs] || '—'}</td>
                  <td className={`${tdClassName} max-w-[280px] break-words`}>{inquiry.serviceInterest || '—'}</td>
                  <td className={tdClassName}>{inquiry.location || '—'}</td>
                  <td className={`${tdClassName} whitespace-nowrap`} title={new Date(inquiry.createdAt).toLocaleString()}>{fmtDate(inquiry.createdAt)}</td>
                  <td className={tdClassName}>
                    <Select
                      className={`min-w-[118px] !rounded-full !px-3 !py-2 text-xs !font-bold !shadow-none !ring-1 ${STATUS_STYLES[inquiry.status] || STATUS_STYLES.new}`}
                      value={inquiry.status}
                      onChange={(event) => changeStatus(inquiry._id, event.target.value)}
                    >
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="archived">Archived</option>
                    </Select>
                  </td>
                  <td className={tdClassName}>
                    <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => remove(inquiry._id)}>
                      <Trash2 size={14} /> Delete
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </TableShell>
    </AdminLayout>
  );
};

export default AdminInquiries;
